import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeSearchInput, toValidObjectIdString } from '../utils/security';
import {
  fetchParcaGonderimler,
  fetchParcaGonderimSekmeSayilari,
  fetchParcaGonderimDetay,
  parcaGonderimKargoGir,
  parcaGonderimTeslimAl,
  dispatchParcaGonderimSayacYenile,
} from '../services/parcaGonderimService';
import GonderimDetayModal from '../components/GonderimDetayModal';
import GonderimOlusturModal from '../components/GonderimOlusturModal';
import TableListFooter from '../components/TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama } from '../utils/pagination';

const AP_TABS = [
  { id: 'onay-bekleyen', label: 'Onay Bekleyen' },
  { id: 'aktif', label: 'Aktif' },
  { id: 'tamamlanan', label: 'Tamamlanan' },
];

const CARGO_ONLY_TABS = [
  { id: 'aktif', label: 'Aktif' },
  { id: 'tamamlanan', label: 'Tamamlanan' },
];

const AP_TAB_IDS = AP_TABS.map((t) => t.id);
const CARGO_ONLY_TAB_IDS = CARGO_ONLY_TABS.map((t) => t.id);

const TP_TABS = [
  { id: 'giden', label: 'Gidenler', yon: 'giden' },
  { id: 'gelen', label: 'Gelenler', yon: 'gelen' },
];

const TP_TAB_IDS = TP_TABS.map((t) => t.id);

const DURUM_LABEL = {
  OnayBekliyor: 'Onay Bekliyor',
  KargoBekliyor: 'Kargo Bekliyor',
  Kargoda: 'Kargoda',
  GonderenTamamlandi: 'Gönderen Tamamlandı',
  AliciBekliyor: 'Alıcı Bekliyor',
  Tamamlandi: 'Tamamlandı',
  IptalEdildi: 'İptal Edildi',
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return '—';
  try {
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    if (!date || isNaN(date.getTime())) return '—';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const sekmeSayisiForTab = (tabId, veri, isAdmin) => {
  if (!veri || typeof veri !== 'object') return null;
  if (isAdmin) return veri[tabId] ?? null;
  if (tabId === 'giden') return veri.giden ?? null;
  if (tabId === 'gelen') return veri.gelen ?? null;
  return null;
};

const resolveUserTechServiceId = (user) => {
  if (!user) return null;
  const ts = user.techService ?? user.techServiceId ?? user.teknikServisId;
  if (!ts) return null;
  if (typeof ts === 'object' && ts !== null) {
    const id = ts._id ?? ts.id;
    return id ? String(id) : null;
  }
  return String(ts);
};

const GonderimlerPage = ({ cargoOnly = false }) => {
  const { user } = useAuth();
  const isSevkiyat = user?.rol === 'sevkiyat' || cargoOnly;
  const isAdmin = user?.rol === 'admin';
  const isApView = isAdmin || isSevkiyat;
  const tabs = isSevkiyat ? CARGO_ONLY_TABS : isAdmin ? AP_TABS : TP_TABS;
  const tabIds = isSevkiyat ? CARGO_ONLY_TAB_IDS : isAdmin ? AP_TAB_IDS : TP_TAB_IDS;

  const [searchParams, setSearchParams] = useSearchParams();
  const urlInitRef = useRef(false);
  const tabFromUrl = searchParams.get('tab');
  const defaultTab = tabs[0].id;
  const initialTab = tabIds.includes(tabFromUrl) ? tabFromUrl : defaultTab;
  const aramaFromUrl = searchParams.get('arama') || '';

  const [gonderimler, setGonderimler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(aramaFromUrl);
  const [filters, setFilters] = useState({ search: aramaFromUrl, tab: initialTab });
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [sekmeSayilari, setSekmeSayilari] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedGonderim, setSelectedGonderim] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const sanitizedSearch = useMemo(
    () => (filters.search && filters.search.trim() ? filters.search.trim() : ''),
    [filters.search],
  );

  const tabConfig = useMemo(() => tabs.find((t) => t.id === filters.tab) || tabs[0], [tabs, filters.tab]);

  const refreshSekmeSayilari = useCallback(async () => {
    try {
      const res = await fetchParcaGonderimSekmeSayilari();
      if (res?.basarili && res?.veri) setSekmeSayilari(res.veri);
    } catch {
      setSekmeSayilari(null);
    }
  }, []);

  const loadGonderimler = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        sayfa: currentPage,
        limit: PAGE_DEFAULT_LIMIT,
        arama: sanitizedSearch || undefined,
      };
      if (isApView) params.tab = tabConfig.id;
      else if (tabConfig.yon) params.yon = tabConfig.yon;

      const res = await fetchParcaGonderimler(params);
      if (res?.basarili && res?.veri) {
        setGonderimler(res.veri.gonderimler || []);
        setPaginationMeta(normalizeSayfalama(res.veri.sayfalama));
      } else {
        setGonderimler([]);
      }
    } catch {
      setGonderimler([]);
    } finally {
      setLoading(false);
    }
  }, [isApView, tabConfig, sanitizedSearch, currentPage]);

  useEffect(() => {
    loadGonderimler();
  }, [loadGonderimler]);

  useEffect(() => {
    refreshSekmeSayilari();
  }, [refreshSekmeSayilari, filters.tab]);

  useEffect(() => {
    if (urlInitRef.current) return;
    urlInitRef.current = true;
    if (!tabFromUrl || !tabIds.includes(tabFromUrl)) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', initialTab);
        if (aramaFromUrl) next.set('arama', aramaFromUrl);
        return next;
      }, { replace: true });
    }
  }, [tabFromUrl, initialTab, aramaFromUrl, tabIds, setSearchParams]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchInput.trim()) next.set('arama', searchInput.trim());
        else next.delete('arama');
        return next;
      }, { replace: true });
    }, 800);
    return () => clearTimeout(timeoutId);
  }, [searchInput, setSearchParams]);

  const reloadAll = useCallback(async () => {
    await loadGonderimler();
    await refreshSekmeSayilari();
    dispatchParcaGonderimSayacYenile();
  }, [loadGonderimler, refreshSekmeSayilari]);

  const openDetail = async (row) => {
    const id = toValidObjectIdString(row?.id ?? row?._id);
    if (!id) {
      window.showNotification?.('error', 'Geçersiz kayıt');
      return;
    }
    try {
      const res = await fetchParcaGonderimDetay(id);
      if (res?.basarili && res?.veri) {
        setSelectedGonderim(res.veri);
        setDetailOpen(true);
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Detay yüklenemedi'));
    }
  };

  const handleKargoKaydet = async (kargoPayload) => {
    const id = toValidObjectIdString(selectedGonderim?.id ?? selectedGonderim?._id);
    if (!id) return;
    setActionLoading(true);
    try {
      await parcaGonderimKargoGir(id, kargoPayload);
      window.showNotification?.('success', 'Kargo bilgisi kaydedildi');
      setDetailOpen(false);
      await reloadAll();
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Kayıt başarısız'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleTeslimAl = async () => {
    const id = toValidObjectIdString(selectedGonderim?.id ?? selectedGonderim?._id);
    if (!id) return;
    setActionLoading(true);
    try {
      await parcaGonderimTeslimAl(id);
      window.showNotification?.('success', 'Gönderim teslim alındı');
      const res = await fetchParcaGonderimDetay(id);
      if (res?.basarili && res?.veri) setSelectedGonderim(res.veri);
      await reloadAll();
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'İşlem başarısız'));
    } finally {
      setActionLoading(false);
    }
  };

  const userTsId = resolveUserTechServiceId(user);

  const isGonderen = (g) => {
    const gid = String(g?.gonderenTeknikServisId ?? g?.gonderenTeknikServis?.id ?? g?.gonderenTeknikServis?._id ?? '');
    return userTsId && gid === userTsId;
  };

  const canEnterKargoFor = (g) => {
    if (!g || g.durum !== 'KargoBekliyor') return false;
    return isAdmin || isSevkiyat || isGonderen(g);
  };

  const isAlici = (g) => {
    const aid = String(g?.aliciTeknikServisId ?? g?.aliciTeknikServis?.id ?? g?.aliciTeknikServis?._id ?? '');
    return userTsId && aid === userTsId;
  };

  const canTeslimAlFor = (g) => {
    if (!g || !['AliciBekliyor', 'GonderenTamamlandi', 'Kargoda'].includes(g.durum)) return false;
    return isSevkiyat || (!isApView && isAlici(g));
  };

  return (
    <main className="main-content gonderimler-page">
      <div className="content-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>Gönderimler</h1>
        </div>
        {isAdmin && !isSevkiyat && (
          <button type="button" className="primary-btn" onClick={() => setCreateOpen(true)} style={{ whiteSpace: 'nowrap' }}>
            <i className="fas fa-plus" style={{ marginRight: 6 }} />
            Gönderim Oluştur
          </button>
        )}
      </div>

      <div className="toolbar" style={{ marginBottom: 16 }}>
        <div className="search-box" style={{ flex: 1, maxWidth: 350 }}>
          <i className="fas fa-search" />
          <input
            type="text"
            placeholder="Gönderim no veya teknik servis ile ara..."
            value={searchInput}
            onChange={(e) => setSearchInput(sanitizeSearchInput(e.target.value, { trimEdges: false }))}
            maxLength={100}
            autoComplete="off"
          />
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {tabs.map((tab) => {
          const n = sekmeSayisiForTab(tab.id, sekmeSayilari, isApView);
          return (
            <button
              key={tab.id}
              type="button"
              className={`tab-btn siparis-tab-with-badge ${filters.tab === tab.id ? 'active' : ''}`}
              onClick={() => {
                setFilters((prev) => ({ ...prev, tab: tab.id }));
                setCurrentPage(1);
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('tab', tab.id);
                  return next;
                }, { replace: true });
              }}
            >
              <span>{tab.label}</span>
              {typeof n === 'number' ? <span className="siparis-tab-badge">{n}</span> : null}
            </button>
          );
        })}
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-gray)' }}>Yükleniyor...</div>
        ) : gonderimler.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-gray)' }}>Kayıt bulunamadı</div>
        ) : (
          <>
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>Gönderim No</th>
                    <th>Gönderen</th>
                    <th>Alıcı</th>
                    <th>Durum</th>
                    <th>Tarih</th>
                    <th className="islemler-sutunu">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {gonderimler.map((g, idx) => {
                    const id = toValidObjectIdString(g?.id ?? g?._id) || `g-${idx}`;
                    return (
                      <tr key={id}>
                        <td style={{ fontWeight: 500 }}>{g.gonderimNumarasi || '—'}</td>
                        <td>{g.gonderenTeknikServisAdi || '—'}</td>
                        <td>{g.aliciTeknikServisAdi || '—'}</td>
                        <td>{DURUM_LABEL[g.durum] || g.durum || '—'}</td>
                        <td>{formatDateTime(g.olusturmaTarihi)}</td>
                        <td className="islemler-sutunu">
                          <button
                            type="button"
                            className="category-action-btn edit-btn"
                            onClick={() => openDetail(g)}
                            title="Detay"
                          >
                            <i className="fas fa-eye" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TableListFooter
              totalRecords={paginationMeta.total}
              currentPage={currentPage}
              totalPages={paginationMeta.totalPages || 1}
              onPageChange={setCurrentPage}
              loading={loading}
              embedded
            />
          </>
        )}
      </div>

      {detailOpen && selectedGonderim && (
        <GonderimDetayModal
          gonderim={selectedGonderim}
          onClose={() => setDetailOpen(false)}
          isAlici={canTeslimAlFor(selectedGonderim)}
          canEnterKargo={canEnterKargoFor(selectedGonderim)}
          onKargoKaydet={canEnterKargoFor(selectedGonderim) ? handleKargoKaydet : undefined}
          onTeslimAl={canTeslimAlFor(selectedGonderim) ? handleTeslimAl : undefined}
          actionLoading={actionLoading}
        />
      )}

      {createOpen && !isSevkiyat && (
        <GonderimOlusturModal
          onClose={() => setCreateOpen(false)}
          onSuccess={reloadAll}
        />
      )}
    </main>
  );
};

export default GonderimlerPage;
